import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const en = {
  meta: {
    homeTitle: "Youtubers Map — An atlas of travel youtubers",
    homeDesc: "A living atlas of travel youtubers. Explore geolocated videos on an interactive map.",
    aboutTitle: "The project · Youtubers Map",
    aboutDesc: "How Youtubers Map works: data sources, AI geolocation, channel selection, update frequency and limitations.",
    youtubersTitle: "Youtubers · Youtubers Map",
    youtubersDesc: "All the travel youtubers collected in Youtubers Map.",
    videosTitle: "Videos · Youtubers Map",
    videosDesc: "Explore all the geolocated videos.",
    countriesTitle: "Countries · Youtubers Map",
    countriesDesc: "All countries covered by our travel youtubers.",
    countryTitle: "Videos in {{name}} · Youtubers Map",
    countryDesc: "All travel youtuber videos filmed in {{name}}: location map, channels, most viewed videos and stats.",
    youtuberTitle: "{{name}} · Youtubers Map",
    youtuberDesc: "Videos and locations of {{name}} on an interactive map.",
    videoTitle: "{{name}} · Youtubers Map",
    videoDesc: "Locations, summary and details of the video: {{name}}.",
  },
  mapCtl: {
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    close: "Close popup",
    enterFullscreen: "View full screen",
    exitFullscreen: "Exit full screen",
  },
  nav: {
    about: "About",
    atlas: "Home",

    explorers: "Youtubers",
    search: "Videos",
    searchPlaceholder: "Search videos, creators, countries…",
    backoffice: "Backoffice",
    countries: "Countries",
    youtubers: "Youtubers",
  },
  common: {
    summary: "Summary",
    description: "Description",
    descriptionOriginal: "Original description",
    transcript: "Transcript",
    locations: "Locations",
    title: "Title",
    views: "Views",
    duration: "Duration",
    country: "Country",
    channel: "Channel",
    youtuber: "Youtuber",
    date: "Date",
    videos: "Videos",
    channels: "Channels",
    all: "All",
    search: "Search…",
    watchOnYoutube: "Watch on YouTube",
    openInYoutube: "Open on YouTube",
    noLocations: "No locations.",
    viewCards: "Cards",
    viewList: "List",
    viewGrid: "Grid",
    perPage: "Per page",
    prev: "Prev",
    next: "Next",
    close: "Close",
    menu: "Menu",
    changeLanguage: "Change language",
    mapMarker: "Map marker: {{label}}",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    showAll: "Show all",
    showLess: "Show less",
    viewOriginalDescription: "View original description",
    viewCleanDescription: "View clean description",
    transcriptEnglish: "English",
    transcriptOriginal: "Original",
   relatedVideos: "Nearby videos",
   sameYoutuber: "From this youtuber",
   otherYoutubers: "From other youtubers",
    backTo: "Back to {{name}}",
  },
  footer: {
    lastUpdate: "Last update: {{date}}",
    tagline: "The travel youtubers, on the map",
  },
  contact: {
    cta: "Contact",
    title: "Contact us",
    desc: "Questions, ideas or anything else? Send us a message.",
    name: "Your name",
    email: "Your email",
    subject: "Subject",
    message: "Message",
    optional: "optional",
    submit: "Send message",
    sending: "Sending…",
    success: "Thanks! We received your message.",
    error: "Could not send the message. Please try again.",
  },
  home: {
    kicker: "Global feed · {{count}} pins live",
    title1: "The world, as filmed by",
    title2: "travel youtubers.",
    tagline: "Discover the places your favourite youtubers have visited.",
    subtitle: "Explore their videos on the map, discover countries, cities and travel stats.",
    exploreCreators: "Explore youtubers",
    searchByCountry: "Browse videos",
    featured: "Youtubers",
    featuredKicker: "Verified field agents",
    viewDirectory: "View directory",
    noCreatorsKicker: "No creators yet",
    noCreatorsBody: "Add your first youtuber from the backoffice to get started.",
    recent: "Latest videos",
    topCountries: "Most visited countries",
    topCountriesKicker: "Global ranking",
    videosCount: "{{count}} videos",
    videoCount_one: "{{count}} video",
    videoCount_other: "{{count}} videos",
    countriesCount: "{{count}} countries",
    viewAll: "See all",
    modeVideos: "Videos",
    modeViews: "Views",
    modeChannels: "Channels",
    mapPoints: "Points",
    mapHeatmap: "Heatmap",
    mapCountries: "Countries",
    mapMode: "Map mode",
    allCountries: "All countries",
    allYears: "All years",
    allChannels: "All channels",
    allYoutubers: "All youtubers",
    mostViewed: "Most viewed",
    views: "views",
    prevPage: "Previous",
    nextPage: "Load more",
    suggest: {
      cta: "Suggest a youtuber",
      title: "Suggest a youtuber",
      desc: "Tell us which travel youtuber we should add. Your details are optional.",
      yourName: "Your name",
      yourEmail: "Your email",
      youtuberName: "Youtuber name",
      channel: "Channel (URL or @handle)",
      notes: "Notes",
      optional: "optional",
      submit: "Send suggestion",
      sending: "Sending…",
      success: "Thanks! We received your suggestion.",
      error: "Could not send the suggestion. Please try again.",
      cancel: "Cancel",
    },
  },
  report: {
    cta: "Report an error",
    title: "Report an error",
    desc: "Is something wrong with this video? Tell us and we'll review it.",
    type: "Issue type",
    typeLabel: "Issue type",
    types: {
      location: "Wrong location",
      country: "Wrong country",
      transcript: "Wrong transcript",
      summary: "Wrong summary",
      description: "Wrong description",
      not_travel: "This video shouldn't be listed",
      other: "Other",
    },
    message: "Describe the issue",
    messagePlaceholder: "For example: the video is in Peru, not Bolivia.",
    email: "Your email",
    optional: "optional",
    submit: "Send report",
    sending: "Sending…",
    success: "Thanks! We received your report.",
    error: "Could not send the report. Please try again.",
  },
  countries: {
    title: "Countries visited",
    kicker: "The atlas",
    subtitle: "Every country in the atlas, ranked by videos, views or channels.",
    videosLabel: "videos",
    viewsLabel: "views",
    channelsLabel: "channels",
    searchPlaceholder: "Search country…",
  },
  explorers: {
    kicker: "The full roster",
    title: "All youtubers",
    empty: "No creators yet. Add some from the",
    backoffice: "backoffice",
    subs: "{{count}} subs",
    grid: "Grid",
    list: "List",
    columns: {
      name: "Name",
      country: "Country",
      subscribers: "Subscribers",
      videos: "Videos",
      countries: "Countries",
    },
  },
  youtuber: {
    back: "← All youtubers",
    since: "Since {{year}}",
    explorer: "Youtuber",
    subscribers: "Subscribers",
    videosTracked: "Videos",
    totalViews: "Total views",
    countriesVisited: "Countries visited",
    avgViews: "Avg. views",
    avgDuration: "Avg. duration",
      firstYear: "First year",
      timeline: "Timeline",
      timelineVideos: "{{count}} videos",
    filters: "Filters",
    allCountries: "All countries ({{count}})",
    allYears: "All years",
    clear: "× Clear",
    videosOf: "{{shown}} of {{total}} videos",
    library: "Video library",
    noMatch: "No videos match these filters.",
    views: "views",
    grid: "Grid",
    list: "List",
    sortBy: "Sort by",
    topCountries: "Top countries",
    sort: {
      recent: "Most recent",
      oldest: "Oldest",
      mostViewed: "Most viewed",
      leastViewed: "Least viewed",
      longest: "Longest",
      shortest: "Shortest",
    },
    channels: "Channels",
    channelsCount_one: "{{count}} channel",
    channelsCount_other: "{{count}} channels",
    allChannels: "All",
  },
  country: {
    title: "Videos in {{country}}",
    stats: "{{videos}} videos · {{youtubers}} youtubers · {{locations}} locations",
    topChannel: "Most videos",
    mostViewed: "Most viewed video",
    latest: "Latest video",
    locationsTitle: "Locations in {{country}}",
  },
  search: {
    kicker: "Browse the archive",
    title: "Videos",
    placeholder: "Filter by title, location…",
    results: "{{count}} results",
    allYoutubers: "All youtubers",
    allCountries: "All countries",
  },
  about: {
    kicker: "About the project",
    title: "How Youtubers Map works",
    intro:
      "Youtubers Map is an independent atlas of travel youtubers. It gathers the videos of the channels we track, works out where each one was filmed and puts them all on a single world map so you can explore the planet through their trips.",
    dataTitle: "Where the data comes from",
    dataBody:
      "Video metadata (title, description, publication date, duration, thumbnail and view count) comes from the public YouTube Data API. When a video has captions we also read the public transcript. We do not host or re-upload any video: everything is embedded from or linked to YouTube, and all content belongs to its creators.",
    geoTitle: "How locations are detected",
    geoBody:
      "For every video a language model reads the title, the cleaned description and, when available, the transcript, and extracts the places that actually appear in the trip. Each place is stored with its country, region, city and coordinates, plus a confidence value and whether it was derived from the description or the transcript. One location per video is marked as primary — it is the one used on the maps and in country rankings. On top of this automatic layer we routinely review the detected locations by hand and, in some cases, assign them manually, so the automatic result is a starting point rather than the final word. The \"report incorrect location\" button on each video feeds directly into this review process: every report tells us where to look and helps us fix errors faster.",
    selectionTitle: "How youtubers are selected",
    selectionBody:
      "We include channels whose main subject is travel: journeys, countries, cultures and places, rather than generic vlogging. Selection is manual and gradual, prioritising channels with a substantial, well documented catalogue. Every creator can have several channels (main channel, second channel, clips), which are grouped under one profile. Anyone can propose a new channel with the \"Suggest a youtuber\" form.",
    freqTitle: "How often it updates",
    freqBody:
      "New videos are imported and geolocated periodically, and view counts are refreshed on the same cycle so the rankings stay meaningful. The date shown as the last update is the publication date of the most recent video in the archive.",
    limitsTitle: "Limitations",
    limitsBody:
      "Automatic geolocation is not perfect: a video can be placed in the wrong city, keep only its most prominent location, or stay without coordinates when there are not enough clues. Videos shorter than a minute and vertical videos (Shorts) are excluded. Transcripts are not available for every video, and coordinates point at a representative spot of the place, not the exact filming point. View counts are a snapshot of the last refresh. If you spot a mistake, use the report button on the video page — it helps a lot.",
    statsTitle: "The archive in numbers",
    statVideos: "Videos",
    statLocations: "Locations",
    statChannels: "Channels",
    statCountries: "Countries",
    contactTitle: "Contact",
    contactBody: "Suggestions, corrections or takedown requests: write to us and we will answer.",
  },
  errors: {
    notFoundKicker: "404 · Off the map",
    notFoundTitle: "Uncharted territory",
    notFoundBody: "This route isn't on our atlas.",
    backHome: "Back to base",
    errorKicker: "Connection error",
    errorTitle: "Something went wrong",
    retry: "Retry",
    base: "Base",
  },
};

const ca = {
  meta: {
    homeTitle: "Youtubers Map — Atles de youtubers viatgers",
    homeDesc: "Un atles viu dels youtubers viatgers. Explora vídeos geolocalitzats en un mapa interactiu.",
    aboutTitle: "El projecte · Youtubers Map",
    aboutDesc: "Com funciona Youtubers Map: fonts de dades, geolocalització amb IA, selecció de canals, freqüència d'actualització i limitacions.",
    youtubersTitle: "Youtubers · Youtubers Map",
    youtubersDesc: "Tots els youtubers viatgers agregats a Youtubers Map.",
    videosTitle: "Vídeos · Youtubers Map",
    videosDesc: "Explora tots els vídeos geolocalitzats.",
    countriesTitle: "Països · Youtubers Map",
    countriesDesc: "Tots els països coberts pels nostres youtubers viatgers.",
    countryTitle: "Vídeos a {{name}} · Youtubers Map",
    countryDesc: "Tots els vídeos de youtubers viatgers gravats a {{name}}: mapa de localitzacions, canals, vídeos més vistos i estadístiques.",
    youtuberTitle: "{{name}} · Youtubers Map",
    youtuberDesc: "Vídeos i localitzacions de {{name}} en un mapa interactiu.",
    videoTitle: "{{name}} · Youtubers Map",
    videoDesc: "Localitzacions, resum i dades del vídeo: {{name}}.",
  },
  mapCtl: {
    zoomIn: "Apropa",
    zoomOut: "Allunya",
    close: "Tanca la finestra",
    enterFullscreen: "Veure a pantalla completa",
    exitFullscreen: "Sortir de la pantalla completa",
  },
  nav: {
    about: "El projecte",
    atlas: "Inici",
    explorers: "Youtubers",
    search: "Vídeos",
    searchPlaceholder: "Cerca vídeos, creadors, països…",
    backoffice: "Backoffice",
    countries: "Països",
    youtubers: "Youtubers",
  },
  common: {
    summary: "Resum",
    description: "Descripció",
    descriptionOriginal: "Descripció original",
    transcript: "Transcripció",
    locations: "Localitzacions",
    title: "Títol",
    views: "Visualitzacions",
    duration: "Durada",
    country: "País",
    channel: "Canal",
    youtuber: "Youtuber",
    date: "Data",
    videos: "Vídeos",
    channels: "Canals",
    all: "Tots",
    search: "Cerca…",
    watchOnYoutube: "Veure a YouTube",
    openInYoutube: "Obrir a YouTube",
    noLocations: "Cap localització.",
    viewCards: "Targetes",
    viewList: "Llista",
    viewGrid: "Graella",
    perPage: "Per pàgina",
    prev: "Anterior",
    next: "Següent",
    close: "Tanca",
    menu: "Menú",
    changeLanguage: "Canvia d'idioma",
    mapMarker: "Marcador del mapa: {{label}}",
    openMenu: "Obre el menú",
    closeMenu: "Tanca el menú",
    showAll: "Veure tots",
    showLess: "Veure'n menys",
    viewOriginalDescription: "Veure descripció original",
    viewCleanDescription: "Veure descripció netejada",
    transcriptEnglish: "Anglès",
    transcriptOriginal: "Original",
   relatedVideos: "Vídeos propers",
   sameYoutuber: "D'aquest youtuber",
   otherYoutubers: "D'altres youtubers",
    backTo: "Tornar a {{name}}",
  },
  footer: {
    lastUpdate: "Última actualització: {{date}}",
    tagline: "Els youtubers viatgers, al mapa",
  },
  contact: {
    cta: "Contacte",
    title: "Contacta amb nosaltres",
    desc: "Dubtes, idees o qualsevol altra cosa? Envia'ns un missatge.",
    name: "El teu nom",
    email: "El teu correu",
    subject: "Assumpte",
    message: "Missatge",
    optional: "opcional",
    submit: "Enviar missatge",
    sending: "Enviant…",
    success: "Gràcies! Hem rebut el teu missatge.",
    error: "No s'ha pogut enviar el missatge. Torna-ho a provar.",
  },
  home: {
    kicker: "Feed global · {{count}} pins en directe",
    title1: "El món, filmat pels",
    title2: "youtubers viatgers.",
    tagline: "Descobreix els llocs que han visitat els teus youtubers preferits.",
    subtitle: "Explora els seus vídeos sobre el mapa, descobreix països, ciutats i estadístiques dels seus viatges.",
    exploreCreators: "Explora youtubers",
    searchByCountry: "Explora vídeos",
    featured: "Youtubers",
    featuredKicker: "Agents de camp verificats",
    viewDirectory: "Veure directori",
    noCreatorsKicker: "Cap creador encara",
    noCreatorsBody: "Afegeix el primer youtuber des del backoffice per començar.",
    recent: "Últims vídeos",
    topCountries: "Països més visitats",
    topCountriesKicker: "Rànquing global",
    videosCount: "{{count}} vídeos",
    videoCount_one: "{{count}} vídeo",
    videoCount_other: "{{count}} vídeos",
    countriesCount: "{{count}} països",
    viewAll: "Veure tots",
    modeVideos: "Vídeos",
    modeViews: "Visualitzacions",
    modeChannels: "Canals",
    mapPoints: "Punts",
    mapHeatmap: "Mapa de calor",
    mapCountries: "Països",
    mapMode: "Mode del mapa",
    allCountries: "Tots els països",
    allYears: "Tots els anys",
    allChannels: "Tots els canals",
    allYoutubers: "Tots els youtubers",
    mostViewed: "Més vistos",
    views: "visualitzacions",
    prevPage: "Anteriors",
    nextPage: "Carrega'n més",
    suggest: {
      cta: "Suggereix un youtuber",
      title: "Suggereix un youtuber",
      desc: "Digues-nos quin youtuber viatger hauríem d'afegir. Les teves dades són opcionals.",
      yourName: "El teu nom",
      yourEmail: "El teu correu",
      youtuberName: "Nom del youtuber",
      channel: "Canal (URL o @handle)",
      notes: "Comentaris",
      optional: "opcional",
      submit: "Enviar suggeriment",
      sending: "Enviant…",
      success: "Gràcies! Hem rebut el teu suggeriment.",
      error: "No s'ha pogut enviar el suggeriment. Torna-ho a provar.",
      cancel: "Cancel·la",
    },
  },
  report: {
    cta: "Informa d'un error",
    title: "Informa d'un error",
    desc: "Hi ha res incorrecte en aquest vídeo? Explica'ns-ho i ho revisarem.",
    type: "Tipus d'error",
    typeLabel: "Tipus d'error",
    types: {
      location: "Localització incorrecta",
      country: "País incorrecte",
      transcript: "Transcripció incorrecta",
      summary: "Resum incorrecte",
      description: "Descripció incorrecta",
      not_travel: "Aquest vídeo no hi hauria de sortir",
      other: "Altre",
    },
    message: "Descriu l'error",
    messagePlaceholder: "Per exemple: el vídeo és al Perú, no a Bolívia.",
    email: "El teu correu",
    optional: "opcional",
    submit: "Enviar informe",
    sending: "Enviant…",
    success: "Gràcies! Hem rebut el teu informe.",
    error: "No s'ha pogut enviar l'informe. Torna-ho a provar.",
  },
  countries: {
    title: "Països visitats",
    kicker: "L'atles",
    subtitle: "Tots els països de l'atles, ordenats per vídeos, visualitzacions o canals.",
    videosLabel: "vídeos",
    viewsLabel: "visualitzacions",
    channelsLabel: "canals",
    searchPlaceholder: "Cerca país…",
  },
  explorers: {
    kicker: "El llistat complet",
    title: "Tots els youtubers",
    empty: "Cap creador encara. Afegeix-ne des del",
    backoffice: "backoffice",
    subs: "{{count}} subs",
    grid: "Graella",
    list: "Llista",
    columns: {
      name: "Nom",
      country: "País",
      subscribers: "Subscriptors",
      videos: "Vídeos",
      countries: "Països",
    },
  },
  youtuber: {
    back: "← Tots els youtubers",
    since: "Des de {{year}}",
    explorer: "Youtuber",
    subscribers: "Subscriptors",
    videosTracked: "Vídeos",
    totalViews: "Visualitzacions totals",
    countriesVisited: "Països visitats",
    avgViews: "Visualitzacions mitjanes",
    avgDuration: "Durada mitjana",
      firstYear: "Any d'inici",
      timeline: "Cronologia",
      timelineVideos: "{{count}} vídeos",
    filters: "Filtres",
    allCountries: "Tots els països ({{count}})",
    allYears: "Tots els anys",
    clear: "× Neteja",
    videosOf: "{{shown}} de {{total}} vídeos",
    library: "Biblioteca de vídeos",
    noMatch: "Cap vídeo coincideix amb aquests filtres.",
    views: "visualitzacions",
    grid: "Graella",
    list: "Llista",
    sortBy: "Ordena per",
    topCountries: "Països més visitats",
    sort: {
      recent: "Més recents",
      oldest: "Més antics",
      mostViewed: "Més vistos",
      leastViewed: "Menys vistos",
      longest: "Més llargs",
      shortest: "Més curts",
    },
    channels: "Canals",
    channelsCount_one: "{{count}} canal",
    channelsCount_other: "{{count}} canals",
    allChannels: "Tots",
  },
  country: {
    title: "Vídeos a {{country}}",
    stats: "{{videos}} vídeos · {{youtubers}} youtubers · {{locations}} localitzacions",
    topChannel: "Més vídeos",
    mostViewed: "Vídeo més vist",
    latest: "Últim vídeo",
    locationsTitle: "Localitzacions a {{country}}",
  },
  search: {
    kicker: "Explora l'arxiu",
    title: "Vídeos",
    placeholder: "Filtra per títol, ubicació…",
    results: "{{count}} resultats",
    allYoutubers: "Tots els youtubers",
    allCountries: "Tots els països",
  },
  about: {
    kicker: "Sobre el projecte",
    title: "Com funciona Youtubers Map",
    intro:
      "Youtubers Map és un atles independent dels youtubers viatgers. Recull els vídeos dels canals que seguim, esbrina on es van gravar i els situa tots en un únic mapa mundial per poder explorar el planeta a través dels seus viatges.",
    dataTitle: "D'on surten les dades",
    dataBody:
      "Les metadades dels vídeos (títol, descripció, data de publicació, durada, miniatura i visualitzacions) provenen de l'API pública de YouTube Data. Quan un vídeo té subtítols, també en llegim la transcripció pública. No allotgem ni republiquem cap vídeo: tot s'incrusta o s'enllaça des de YouTube i el contingut és dels seus creadors.",
    geoTitle: "Com es detecten les localitzacions",
    geoBody:
      "Per a cada vídeo, un model de llenguatge llegeix el títol, la descripció neta i, si n'hi ha, la transcripció, i extreu els llocs que realment apareixen al viatge. Cada lloc es guarda amb país, regió, ciutat i coordenades, amb un valor de confiança i indicant si s'ha deduït de la descripció o de la transcripció. Una localització per vídeo es marca com a principal: és la que s'utilitza als mapes i als rànquings de països. A sobre d'aquesta capa automàtica hi ha una revisió manual: fem una passada regular per totes les localitzacions detectades i, en alguns casos, les assignem a mà, de manera que el resultat automàtic és un punt de partida, no la paraula final. El botó per informar de localitzacions incorrectes de cada vídeo s'integra directament en aquest procés de revisió: cada informe ens diu on mirar i ens ajuda a corregir possibles errors més ràpid.",
    selectionTitle: "Com se seleccionen els youtubers",
    selectionBody:
      "Incloem canals el tema principal dels quals és el viatge: rutes, països, cultures i llocs, més que el vlog genèric. La selecció és manual i gradual, prioritzant canals amb un catàleg ampli i ben documentat. Cada creador pot tenir diversos canals (principal, segon canal, clips), que s'agrupen en un mateix perfil. Qualsevol persona pot proposar un canal nou amb el formulari \"Proposa un youtuber\".",
    freqTitle: "Amb quina freqüència s'actualitza",
    freqBody:
      "Els vídeos nous s'importen i es geolocalitzen periòdicament, i les visualitzacions es refresquen en el mateix cicle perquè els rànquings segueixin tenint sentit. La data que es mostra com a última actualització és la data de publicació del vídeo més recent de l'arxiu.",
    limitsTitle: "Limitacions",
    limitsBody:
      "La geolocalització automàtica no és perfecta: un vídeo pot quedar situat en una ciutat equivocada, conservar només la localització més destacada o quedar sense coordenades si no hi ha prou pistes. Els vídeos de menys d'un minut i els verticals (Shorts) queden exclosos. No tots els vídeos tenen transcripció, i les coordenades apunten a un punt representatiu del lloc, no al punt exacte de gravació. Les visualitzacions són una fotografia de l'últim refresc. Si hi veus un error, fes servir el botó de report a la pàgina del vídeo: ajuda molt.",
    statsTitle: "L'arxiu en xifres",
    statVideos: "Vídeos",
    statLocations: "Localitzacions",
    statChannels: "Canals",
    statCountries: "Països",
    contactTitle: "Contacte",
    contactBody: "Suggeriments, correccions o peticions de retirada: escriu-nos i et respondrem.",
  },
  errors: {
    notFoundKicker: "404 · Fora del mapa",
    notFoundTitle: "Territori inexplorat",
    notFoundBody: "Aquesta ruta no apareix al nostre atles.",
    backHome: "Torna a base",
    errorKicker: "Error de connexió",
    errorTitle: "Alguna cosa ha fallat",
    retry: "Reintenta",
    base: "Base",
  },
};

const es = {
  meta: {
    homeTitle: "Youtubers Map — Atlas de youtubers viajeros",
    homeDesc: "Un atlas vivo de los youtubers viajeros. Explora vídeos geolocalizados en un mapa interactivo.",
    aboutTitle: "El proyecto · Youtubers Map",
    aboutDesc: "Cómo funciona Youtubers Map: fuentes de datos, geolocalización con IA, selección de canales, frecuencia de actualización y limitaciones.",
    youtubersTitle: "Youtubers · Youtubers Map",
    youtubersDesc: "Todos los youtubers viajeros reunidos en Youtubers Map.",
    videosTitle: "Vídeos · Youtubers Map",
    videosDesc: "Explora todos los vídeos geolocalizados.",
    countriesTitle: "Países · Youtubers Map",
    countriesDesc: "Todos los países cubiertos por nuestros youtubers viajeros.",
    countryTitle: "Vídeos en {{name}} · Youtubers Map",
    countryDesc: "Todos los vídeos de youtubers viajeros grabados en {{name}}: mapa de localizaciones, canales, vídeos más vistos y estadísticas.",
    youtuberTitle: "{{name}} · Youtubers Map",
    youtuberDesc: "Vídeos y localizaciones de {{name}} en un mapa interactivo.",
    videoTitle: "{{name}} · Youtubers Map",
    videoDesc: "Localizaciones, resumen y datos del vídeo: {{name}}.",
  },
  mapCtl: {
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    close: "Cerrar ventana",
    enterFullscreen: "Ver a pantalla completa",
    exitFullscreen: "Salir de la pantalla completa",
  },
  nav: {
    about: "El proyecto",
    atlas: "Inicio",
    explorers: "Youtubers",
    search: "Vídeos",
    searchPlaceholder: "Busca vídeos, creadores, países…",
    backoffice: "Backoffice",
    countries: "Países",
    youtubers: "Youtubers",
  },
  common: {
    summary: "Resumen",
    description: "Descripción",
    descriptionOriginal: "Descripción original",
    transcript: "Transcripción",
    locations: "Localizaciones",
    title: "Título",
    views: "Visualizaciones",
    duration: "Duración",
    country: "País",
    channel: "Canal",
    youtuber: "Youtuber",
    date: "Fecha",
    videos: "Vídeos",
    channels: "Canales",
    all: "Todos",
    search: "Buscar…",
    watchOnYoutube: "Ver en YouTube",
    openInYoutube: "Abrir en YouTube",
    noLocations: "Sin localizaciones.",
    viewCards: "Tarjetas",
    viewList: "Lista",
    viewGrid: "Cuadrícula",
    perPage: "Por página",
    prev: "Anterior",
    next: "Siguiente",
    close: "Cerrar",
    menu: "Menú",
    changeLanguage: "Cambiar idioma",
    mapMarker: "Marcador del mapa: {{label}}",
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    showAll: "Ver todos",
    showLess: "Ver menos",
    viewOriginalDescription: "Ver descripción original",
    viewCleanDescription: "Ver descripción limpia",
    transcriptEnglish: "Inglés",
    transcriptOriginal: "Original",
   relatedVideos: "Vídeos cercanos",
   sameYoutuber: "De este youtuber",
   otherYoutubers: "De otros youtubers",
    backTo: "Volver a {{name}}",
  },
  footer: {
    lastUpdate: "Última actualización: {{date}}",
    tagline: "Los youtubers viajeros, en el mapa",
  },
  contact: {
    cta: "Contacto",
    title: "Contacta con nosotros",
    desc: "¿Dudas, ideas o cualquier otra cosa? Envíanos un mensaje.",
    name: "Tu nombre",
    email: "Tu correo",
    subject: "Asunto",
    message: "Mensaje",
    optional: "opcional",
    submit: "Enviar mensaje",
    sending: "Enviando…",
    success: "¡Gracias! Hemos recibido tu mensaje.",
    error: "No se ha podido enviar el mensaje. Inténtalo de nuevo.",
  },
  home: {
    kicker: "Feed global · {{count}} pins en directo",
    title1: "El mundo, filmado por",
    title2: "youtubers viajeros.",
    tagline: "Descubre los lugares que han visitado tus youtubers favoritos.",
    subtitle: "Explora sus vídeos sobre el mapa, descubre países, ciudades y estadísticas de sus viajes.",
    exploreCreators: "Explorar youtubers",
    searchByCountry: "Explorar vídeos",
    featured: "Youtubers",
    featuredKicker: "Agentes de campo verificados",
    viewDirectory: "Ver directorio",
    noCreatorsKicker: "Aún no hay creadores",
    noCreatorsBody: "Añade el primer youtuber desde el backoffice para empezar.",
    recent: "Últimos vídeos",
    topCountries: "Países más visitados",
    topCountriesKicker: "Ranking global",
    videosCount: "{{count}} vídeos",
    videoCount_one: "{{count}} vídeo",
    videoCount_other: "{{count}} vídeos",
    countriesCount: "{{count}} países",
    viewAll: "Ver todos",
    modeVideos: "Vídeos",
    modeViews: "Visualizaciones",
    modeChannels: "Canales",
    mapPoints: "Puntos",
    mapHeatmap: "Mapa de calor",
    mapCountries: "Países",
    mapMode: "Modo de mapa",
    allCountries: "Todos los países",
    allYears: "Todos los años",
    allChannels: "Todos los canales",
    allYoutubers: "Todos los youtubers",
    mostViewed: "Más vistos",
    views: "visualizaciones",
    prevPage: "Anteriores",
    nextPage: "Cargar más",
    suggest: {
      cta: "Sugiere un youtuber",
      title: "Sugiere un youtuber",
      desc: "Dinos qué youtuber viajero deberíamos añadir. Tus datos son opcionales.",
      yourName: "Tu nombre",
      yourEmail: "Tu email",
      youtuberName: "Nombre del youtuber",
      channel: "Canal (URL o @handle)",
      notes: "Comentarios",
      optional: "opcional",
      submit: "Enviar sugerencia",
      sending: "Enviando…",
      success: "¡Gracias! Hemos recibido tu sugerencia.",
      error: "No se ha podido enviar la sugerencia. Inténtalo de nuevo.",
      cancel: "Cancelar",
    },
  },
  report: {
    cta: "Informar de un error",
    title: "Informar de un error",
    desc: "¿Hay algo incorrecto en este vídeo? Cuéntanoslo y lo revisaremos.",
    type: "Tipo de error",
    typeLabel: "Tipo de error",
    types: {
      location: "Localización incorrecta",
      country: "País incorrecto",
      transcript: "Transcripción incorrecta",
      summary: "Resumen incorrecto",
      description: "Descripción incorrecta",
      not_travel: "Este vídeo no debería aparecer",
      other: "Otro",
    },
    message: "Describe el error",
    messagePlaceholder: "Por ejemplo: el vídeo es en Perú, no en Bolivia.",
    email: "Tu email",
    optional: "opcional",
    submit: "Enviar informe",
    sending: "Enviando…",
    success: "¡Gracias! Hemos recibido tu informe.",
    error: "No se ha podido enviar el informe. Inténtalo de nuevo.",
  },
  countries: {
    title: "Países visitados",
    kicker: "El atlas",
    subtitle: "Todos los países del atlas, ordenados por vídeos, visualizaciones o canales.",
    videosLabel: "vídeos",
    viewsLabel: "visualizaciones",
    channelsLabel: "canales",
    searchPlaceholder: "Buscar país…",
  },
  explorers: {
    kicker: "El listado completo",
    title: "Todos los youtubers",
    empty: "Aún no hay creadores. Añade alguno desde el",
    backoffice: "backoffice",
    subs: "{{count}} subs",
    grid: "Cuadrícula",
    list: "Lista",
    columns: {
      name: "Nombre",
      country: "País",
      subscribers: "Suscriptores",
      videos: "Vídeos",
      countries: "Países",
    },
  },
  youtuber: {
    back: "← Todos los youtubers",
    since: "Desde {{year}}",
    explorer: "Youtuber",
    subscribers: "Suscriptores",
    videosTracked: "Vídeos",
    totalViews: "Visualizaciones totales",
    countriesVisited: "Países visitados",
    avgViews: "Visualizaciones medias",
    avgDuration: "Duración media",
      firstYear: "Año de inicio",
      timeline: "Cronología",
      timelineVideos: "{{count}} vídeos",
    filters: "Filtros",
    allCountries: "Todos los países ({{count}})",
    allYears: "Todos los años",
    clear: "× Limpiar",
    videosOf: "{{shown}} de {{total}} vídeos",
    library: "Biblioteca de vídeos",
    noMatch: "Ningún vídeo coincide con estos filtros.",
    views: "visualizaciones",
    grid: "Cuadrícula",
    list: "Lista",
    sortBy: "Ordenar por",
    topCountries: "Países más visitados",
    sort: {
      recent: "Más recientes",
      oldest: "Más antiguos",
      mostViewed: "Más vistos",
      leastViewed: "Menos vistos",
      longest: "Más largos",
      shortest: "Más cortos",
    },
    channels: "Canales",
    channelsCount_one: "{{count}} canal",
    channelsCount_other: "{{count}} canales",
    allChannels: "Todos",
  },
  country: {
    title: "Vídeos en {{country}}",
    stats: "{{videos}} vídeos · {{youtubers}} youtubers · {{locations}} localizaciones",
    topChannel: "Más vídeos",
    mostViewed: "Vídeo más visto",
    latest: "Último vídeo",
    locationsTitle: "Localizaciones en {{country}}",
  },
  search: {
    kicker: "Explora el archivo",
    title: "Vídeos",
    placeholder: "Filtra por título, ubicación…",
    results: "{{count}} resultados",
    allYoutubers: "Todos los youtubers",
    allCountries: "Todos los países",
  },
  about: {
    kicker: "Sobre el proyecto",
    title: "Cómo funciona Youtubers Map",
    intro:
      "Youtubers Map es un atlas independiente de los youtubers viajeros. Reúne los vídeos de los canales que seguimos, averigua dónde se grabaron y los sitúa todos en un único mapa mundial para explorar el planeta a través de sus viajes.",
    dataTitle: "De dónde salen los datos",
    dataBody:
      "Los metadatos de los vídeos (título, descripción, fecha de publicación, duración, miniatura y visualizaciones) provienen de la API pública de YouTube Data. Cuando un vídeo tiene subtítulos, también leemos su transcripción pública. No alojamos ni republicamos ningún vídeo: todo se incrusta o enlaza desde YouTube y el contenido pertenece a sus creadores.",
    geoTitle: "Cómo se detectan las localizaciones",
    geoBody:
      "Para cada vídeo, un modelo de lenguaje lee el título, la descripción limpia y, si existe, la transcripción, y extrae los lugares que realmente aparecen en el viaje. Cada lugar se guarda con país, región, ciudad y coordenadas, con un valor de confianza e indicando si se dedujo de la descripción o de la transcripción. Una localización por vídeo se marca como principal: es la que se usa en los mapas y en los rankings de países. Sobre esta capa automática hay una revisión manual: repasamos periódicamente todas las localizaciones detectadas y, en algunos casos, las asignamos a mano, de modo que el resultado automático es un punto de partida, no la palabra final. El botón para informar de localizaciones incorrectas de cada vídeo se integra directamente en este proceso de revisión: cada aviso nos indica dónde mirar y nos ayuda a corregir posibles errores más rápido.",
    selectionTitle: "Cómo se seleccionan los youtubers",
    selectionBody:
      "Incluimos canales cuyo tema principal es el viaje: rutas, países, culturas y lugares, más que el vlog genérico. La selección es manual y gradual, priorizando canales con un catálogo amplio y bien documentado. Cada creador puede tener varios canales (principal, segundo canal, clips), que se agrupan en un mismo perfil. Cualquiera puede proponer un canal nuevo con el formulario \"Propón un youtuber\".",
    freqTitle: "Con qué frecuencia se actualiza",
    freqBody:
      "Los vídeos nuevos se importan y geolocalizan periódicamente, y las visualizaciones se refrescan en el mismo ciclo para que los rankings sigan teniendo sentido. La fecha que se muestra como última actualización es la fecha de publicación del vídeo más reciente del archivo.",
    limitsTitle: "Limitaciones",
    limitsBody:
      "La geolocalización automática no es perfecta: un vídeo puede quedar situado en una ciudad equivocada, conservar solo la localización más destacada o quedarse sin coordenadas si no hay suficientes pistas. Los vídeos de menos de un minuto y los verticales (Shorts) quedan excluidos. No todos los vídeos tienen transcripción, y las coordenadas apuntan a un punto representativo del lugar, no al punto exacto de grabación. Las visualizaciones son una foto del último refresco. Si ves un error, usa el botón de reporte en la página del vídeo: ayuda mucho.",
    statsTitle: "El archivo en cifras",
    statVideos: "Vídeos",
    statLocations: "Localizaciones",
    statChannels: "Canales",
    statCountries: "Países",
    contactTitle: "Contacto",
    contactBody: "Sugerencias, correcciones o peticiones de retirada: escríbenos y te responderemos.",
  },
  errors: {
    notFoundKicker: "404 · Fuera del mapa",
    notFoundTitle: "Territorio inexplorado",
    notFoundBody: "Esta ruta no aparece en nuestro atlas.",
    backHome: "Volver a base",
    errorKicker: "Error de conexión",
    errorTitle: "Algo ha fallado",
    retry: "Reintentar",
    base: "Base",
  },
};

if (!i18n.isInitialized) {
  (i18n as any).use(initReactI18next).init({
    resources: {
      en: { translation: en },
      ca: { translation: ca },
      es: { translation: es },
    },
    // The real language is resolved on the server per request (cookie /
    // Accept-Language, see lang.functions.ts) and applied with setLanguage()
    // before rendering, so server HTML and hydration always match.
    lng: "es",
    fallbackLng: "es",
    supportedLngs: ["es", "ca", "en"],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    initImmediate: false,
  });
}

/**
 * Synchronously switch language (resources are bundled, so no async load).
 * Client-only: on the server the global instance is shared by concurrent
 * requests, so it must never be mutated there (use getI18nFor instead).
 */
export function setLanguage(code: string) {
  if (typeof window === "undefined") return;
  if (i18n.language !== code) void i18n.changeLanguage(code);
}

const serverInstances = new Map<string, typeof i18n>();
let clientLangApplied = false;

/**
 * Instance to render with. Server: one fixed-language clone per language,
 * never mutated, so concurrent requests can't leak languages into each other.
 * Client: the global instance switched to `code`.
 */
export function getI18nFor(code: string): typeof i18n {
  const lng = ["es", "ca", "en"].includes(code) ? code : "es";
  if (typeof window !== "undefined") {
    // Only the first render takes the server's language; later switches made
    // by the visitor must not be undone by re-renders.
    if (!clientLangApplied) {
      clientLangApplied = true;
      setLanguage(lng);
    }
    return i18n;
  }
  let inst = serverInstances.get(lng);
  if (!inst) {
    inst = i18n.cloneInstance({ lng });
    serverInstances.set(lng, inst);
  }
  return inst;
}

/** Persist an explicit choice so the server renders it on the next visit. */
export function rememberLanguage(code: string) {
  if (typeof document === "undefined") return;
  document.cookie = `ym_lang=${code}; path=/; max-age=31536000; samesite=lax`;
  try {
    localStorage.setItem("ym_lang_user", code);
  } catch {}
}

/** Migrate an old localStorage-only choice into the cookie. Client-only. */
export function migrateStoredLanguage() {
  if (typeof window === "undefined") return;
  if (document.cookie.includes("ym_lang=")) return;
  try {
    const stored = localStorage.getItem("ym_lang_user");
    if (stored && ["es", "ca", "en"].includes(stored)) {
      rememberLanguage(stored);
      setLanguage(stored);
    }
  } catch {}
}

export default i18n;

// Figures band labels: about.statVideos / statLocations / statChannels / statCountries.
